import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOptionalAuth } from "@/contexts/AuthContext";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface UseChatbotReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  sendMessage: (text: string) => Promise<void>;
  clearChat: () => void;
  sessionRemaining: number;
  dailyRemaining: number;
  error: string | null;
}

const SESSION_LIMIT = 15;
const DAILY_LIMIT_DEFAULT = 20;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function generateSessionId(): string {
  const stored = sessionStorage.getItem("chatbot_session_id");
  if (stored) return stored;
  const newId = generateId();
  sessionStorage.setItem("chatbot_session_id", newId);
  return newId;
}

export function useChatbot(): UseChatbotReturn {
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [sessionRemaining, setSessionRemaining] = useState(SESSION_LIMIT);
  const [dailyRemaining, setDailyRemaining] = useState(DAILY_LIMIT_DEFAULT);
  const [error, setError] = useState<string | null>(null);
  
  const sessionIdRef = useRef<string>(generateSessionId());
  const abortControllerRef = useRef<AbortController | null>(null);

  // Greeting message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = user
        ? `Olá! Sou a Pronty, sua assistente virtual do ProntuÁudio! 🤖 Como posso ajudar você hoje?`
        : `Olá! Sou a Pronty, sua assistente virtual do ProntuÁudio! 🤖 Como posso ajudar você hoje? Se você já tem uma conta, entre para que eu possa dar respostas mais personalizadas!`;
      
      setMessages([
        {
          id: generateId(),
          role: "assistant",
          content: greeting,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, user, messages.length]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    setError(null);
    
    // Add user message
    const userMessage: ChatMessage = {
      id: generateId(),
      role: "user",
      content: text.trim(),
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Prepare messages for API (excluding greeting if it's the only assistant message)
    const apiMessages = [...messages, userMessage]
      .filter(m => m.role === "user" || messages.indexOf(m as ChatMessage) > 0)
      .map(m => ({ role: m.role, content: m.content }));

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    let assistantContent = "";

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/support-chatbot`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            messages: apiMessages.length > 0 ? apiMessages : [{ role: "user", content: text.trim() }],
            sessionId: sessionIdRef.current,
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 429) {
          if (errorData.blockedReason === "session_limit") {
            setSessionRemaining(0);
          } else {
            setDailyRemaining(0);
          }
          throw new Error(errorData.error || "Limite de mensagens atingido");
        }
        
        throw new Error(errorData.error || "Erro ao enviar mensagem");
      }

      // Stream response
      const reader = response.body?.getReader();
      if (!reader) throw new Error("Streaming não suportado");

      const decoder = new TextDecoder();
      let buffer = "";

      // Add assistant message placeholder
      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE lines
        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages(prev => 
                prev.map((m, i) => 
                  i === prev.length - 1 
                    ? { ...m, content: assistantContent }
                    : m
                )
              );
            }
          } catch {
            // Incomplete JSON, put back and wait for more data
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }

      // Update remaining counts
      setSessionRemaining(prev => Math.max(0, prev - 1));
      setDailyRemaining(prev => Math.max(0, prev - 1));

    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      
      const errorMessage = err instanceof Error ? err.message : "Erro ao enviar mensagem";
      setError(errorMessage);
      
      // Add error message to chat
      setMessages(prev => [
        ...prev,
        {
          id: generateId(),
          role: "assistant",
          content: `Desculpe, ocorreu um erro: ${errorMessage}. Por favor, tente novamente ou entre em contato com contato.prontuaudio@inspiracriativa.com.br`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [messages, isLoading, user]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    // Generate new session ID
    const newSessionId = generateId();
    sessionStorage.setItem("chatbot_session_id", newSessionId);
    sessionIdRef.current = newSessionId;
    setSessionRemaining(SESSION_LIMIT);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    messages,
    isLoading,
    isOpen,
    setIsOpen,
    sendMessage,
    clearChat,
    sessionRemaining,
    dailyRemaining,
    error,
  };
}
